import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "~/lib/fetchClient";

const PERIOD_KEYS = { today: "today", this_week: "thisWeek", next_week: "nextWeek", last_week: "lastWeek" };
const refreshListeners = new Set();

export const refreshAppointments = (options = {}) => {
  refreshListeners.forEach((listener) => listener(options));
};

export default function useAppointments({ ownerScope, product }) {
  const [appointmentsByPeriod, setAppointmentsByPeriod] = useState({});
  const [appointmentCounts, setAppointmentCounts] = useState({});
  const [loadingPeriods, setLoadingPeriods] = useState({});
  const lastPeriodRef = useRef("today");
  const activeScopeRef = useRef(ownerScope);

  const loadPeriod = useCallback(async (period, force = false, requestedScope = ownerScope) => {
    lastPeriodRef.current = period;
    if (requestedScope !== activeScopeRef.current) {
      activeScopeRef.current = requestedScope;
      setAppointmentsByPeriod({});
      setAppointmentCounts({});
      force = true;
    }
    if (!force && Object.prototype.hasOwnProperty.call(appointmentsByPeriod, period)) return;

    setLoadingPeriods((state) => ({ ...state, [period]: true }));
    try {
      const response = await apiFetch.get("/appointments", {
        params: { lead_scope: requestedScope, product, period },
      });
      const responseKey = PERIOD_KEYS[period];
      setAppointmentsByPeriod((state) => ({ ...state, [period]: response[responseKey] || response[period] || [] }));
      setAppointmentCounts((state) => ({ ...state, ...(response.counts || {}) }));
    } finally {
      setLoadingPeriods((state) => ({ ...state, [period]: false }));
    }
  }, [appointmentsByPeriod, ownerScope, product]);

  useEffect(() => {
    const listener = (options) => loadPeriod(
      options.period || lastPeriodRef.current,
      true,
      options.ownerScope || ownerScope,
    );
    refreshListeners.add(listener);
    return () => refreshListeners.delete(listener);
  }, [loadPeriod, ownerScope]);

  const deleteAppointment = useCallback(async (id, period) => {
    await apiFetch.delete(`/appointments/${id}`);
    setAppointmentsByPeriod((state) => ({ ...state, [period]: (state[period] || []).filter((appointment) => appointment.id !== id) }));
    await loadPeriod(period, true);
  }, [loadPeriod]);

  return { appointmentsByPeriod, appointmentCounts, loadingPeriods, loadPeriod, deleteAppointment };
}
